/**
 * Plays the muted inline videos (video[data-babalu-autoplay], rendered by
 * snippets/babalu-inline-video.liquid) only while they are on screen.
 *
 * - ~600px before the viewport: the poster is set from data-poster and the
 *   video starts buffering, so it plays the moment it shows.
 * - At least 25% visible: muted play; leaving the viewport pauses it, so a
 *   long collection grid never decodes more videos than fit on screen
 *   (iOS Safari stalls when many play at once).
 * - Safari can refuse autoplay (Low Power Mode, data saver): the video is
 *   retried when it can play and on every touch, scroll or click (iOS Low
 *   Power Mode only allows play() inside a completed tap: touchend/click).
 * - Cards added later (infinite scroll, load more, filters, quick view) are
 *   picked up by a MutationObserver.
 * - Anything else that plays an off-screen video (the theme card resumes
 *   it on mouseleave) is paused right away.
 * - Color swatches on a video card: the theme writes the color's photo into
 *   the hidden img[data-babalu-video-fallback]; the video is then paused
 *   and hidden and the photo shown, as on a card that starts with a photo.
 *
 * Author: NOVADEHA / Felipe Ocampo
 */
(function () {
  'use strict';

  // Inline videos (cards, carousel) and the product gallery video when the
  // gallery has media autoplay on (Chrome never got play() from the theme).
  var SELECTOR = 'video[data-babalu-autoplay], hdt-product-media hdt-video-player[autoplay] video';
  var seen = new WeakSet();
  var visible = new Set();
  var blocked = new Set();

  function setPoster(video) {
    var poster = video.getAttribute('data-poster');
    if (poster) {
      video.setAttribute('poster', poster);
      video.removeAttribute('data-poster');
    }
  }

  // Buffer at most MAX_BUFFERING videos ahead of time: when infinite scroll
  // adds a batch, buffering them all at once made each one start late.
  // A video on screen skips the queue.
  var MAX_BUFFERING = 2;
  var buffering = new Set();
  var queue = [];

  function startBuffering(video) {
    video.preload = 'auto';
    if (video.readyState === 0) video.load();
    if (video.readyState >= 3) return;
    buffering.add(video);
    var done = function () {
      if (!buffering.delete(video)) return;
      pump();
    };
    video.addEventListener('canplay', done, { once: true });
    video.addEventListener('error', done, { once: true });
    setTimeout(done, 8000);
  }

  function pump() {
    while (buffering.size < MAX_BUFFERING && queue.length) {
      var next = queue.shift();
      if (next.isConnected && next.preload !== 'auto') startBuffering(next);
    }
  }

  function prepare(video, urgent) {
    setPoster(video);
    if (video.preload === 'auto') return;
    if (urgent || buffering.size < MAX_BUFFERING) {
      var index = queue.indexOf(video);
      if (index > -1) queue.splice(index, 1);
      startBuffering(video);
    } else if (queue.indexOf(video) === -1) {
      queue.push(video);
    }
  }

  function tryPlay(video) {
    if (!video.paused || !visible.has(video)) return;
    // iOS needs the muted *property*, not only the attribute.
    video.muted = true;
    video.playsInline = true;
    if (video.preload !== 'auto') prepare(video, true);
    var attempt;
    try {
      attempt = video.play();
    } catch (error) {
      blocked.add(video);
      return;
    }
    if (attempt && typeof attempt.then === 'function') {
      attempt.then(function () {
        blocked.delete(video);
      }, function () {
        blocked.add(video);
      });
    }
  }

  function retryBlocked() {
    blocked.forEach(tryPlay);
  }

  if (!('IntersectionObserver' in window)) {
    var startAll = function () {
      document.querySelectorAll(SELECTOR).forEach(function (video) {
        setPoster(video);
        visible.add(video);
        tryPlay(video);
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startAll);
    } else {
      startAll();
    }
    return;
  }

  var nearObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      prepare(entry.target);
      nearObserver.unobserve(entry.target);
    });
  }, { rootMargin: '600px 0px' });

  var playObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (entry.isIntersecting) {
        visible.add(video);
        tryPlay(video);
      } else {
        visible.delete(video);
        blocked.delete(video);
        if (!video.paused) video.pause();
      }
    });
  }, { threshold: 0.25 });

  function watch(video) {
    if (seen.has(video)) return;
    seen.add(video);
    nearObserver.observe(video);
    playObserver.observe(video);
    video.addEventListener('canplay', function () {
      tryPlay(video);
    });
    // The theme card plays its main video again on mouseleave, even when
    // the page has scrolled it away: keep off-screen videos paused.
    video.addEventListener('play', function () {
      if (!visible.has(video)) video.pause();
    });
  }

  var FALLBACK = 'img[data-babalu-video-fallback]';
  var fallbacks = new WeakSet();
  var swapObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      showColorImage(mutation.target);
    });
  });

  function showColorImage(img) {
    if (!img.hasAttribute('hidden')) return;
    var wrap = img.closest('.hdt-card-product__media-wrapp') || img.parentElement;
    var player = wrap && wrap.querySelector('hdt-video-player[main-media]');
    var video = player && player.querySelector('video');
    if (video) {
      playObserver.unobserve(video);
      visible.delete(video);
      blocked.delete(video);
      video.removeAttribute('data-babalu-autoplay');
      video.pause();
    }
    if (player) {
      player.hidden = true;
      player.style.display = 'none';
      player.removeAttribute('main-media');
    }
    img.removeAttribute('hidden');
    img.loading = 'eager';
  }

  function watchFallback(img) {
    if (fallbacks.has(img)) return;
    fallbacks.add(img);
    swapObserver.observe(img, { attributes: true, attributeFilter: ['src', 'srcset'] });
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches && root.matches(SELECTOR)) watch(root);
    if (root.querySelectorAll) root.querySelectorAll(SELECTOR).forEach(watch);
    if (root.matches && root.matches(FALLBACK)) watchFallback(root);
    if (root.querySelectorAll) root.querySelectorAll(FALLBACK).forEach(watchFallback);
  }

  function start() {
    scan(document.body);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(scan);
        mutation.removedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          visible.forEach(function (video) {
            if (!video.isConnected) {
              visible.delete(video);
              blocked.delete(video);
            }
          });
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    ['touchstart', 'touchend', 'pointerdown', 'click', 'scroll', 'keydown'].forEach(function (type) {
      window.addEventListener(type, retryBlocked, { passive: true });
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') visible.forEach(tryPlay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
